import React, { useState, useEffect } from 'react';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Building2, Plus, Search, Edit, Trash2, Loader2, ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TenantFilter from '../components/TenantFilter';

const CustomersPage = () => {
  const { token, hasRole } = useAuth();
  const { selectedTenant, getTenantParam } = useTenant();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    active: true,
    pricing: [{
      service_type: 'socorro',
      base_price: 750,
      time_franchise_minutes: 60,
      distance_franchise_km: 50,
      extra_hour_rate: 150,
      extra_km_rate: 8
    }]
  });

  useEffect(() => {
    fetchCustomers();
  }, [token, selectedTenant]);

  const fetchCustomers = async () => {
    try {
      const data = await api.get(`/customers${getTenantParam()}`, token);
      setCustomers(data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, formData, token);
        toast.success('Cliente atualizado');
      } else {
        await api.post('/customers', formData, token);
        toast.success('Cliente criado');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;
    try {
      await api.delete(`/customers/${id}`, token);
      toast.success('Cliente removido');
      fetchCustomers();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      cnpj: customer.cnpj,
      email: customer.email,
      phone: customer.phone,
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      active: customer.active,
      pricing: customer.pricing?.length > 0 ? customer.pricing : [{
        service_type: 'socorro', base_price: 750, time_franchise_minutes: 60, distance_franchise_km: 50, extra_hour_rate: 150, extra_km_rate: 8
      }]
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: '', cnpj: '', email: '', phone: '', address: '', city: '', state: '', active: true,
      pricing: [{ service_type: 'socorro', base_price: 750, time_franchise_minutes: 60, distance_franchise_km: 50, extra_hour_rate: 150, extra_km_rate: 8 }]
    });
  };

  const updatePricing = (field, value) => {
    setFormData({
      ...formData,
      pricing: [{ ...formData.pricing[0], [field]: value }]
    });
  };

  const filteredCustomers = customers
    .filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  const canEdit = hasRole(['mestre', 'adm', 'financeiro']);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Clientes
          </h1>
          <p className="text-slate-500 mt-1">{customers.length} clientes cadastrados</p>
        </div>
        <div className="flex items-center gap-3">
          <TenantFilter />
          {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-customer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Public Sans' }}>
                  {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>CNPJ</Label>
                    <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} required data-testid="customer-cnpj-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="customer-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="customer-phone-input" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Endereço</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="customer-address-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} data-testid="customer-city-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} maxLength={2} data-testid="customer-state-input" />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium text-slate-900 mb-4">Tabela de Preços (Cobrança ao Cliente)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Valor Base por Missão (R$)</Label>
                      <Input type="number" step="0.01" value={formData.pricing[0]?.base_price || 750} onChange={(e) => updatePricing('base_price', parseFloat(e.target.value))} data-testid="customer-base-price-input" className="text-lg font-semibold" />
                      <p className="text-xs text-slate-500">Valor fixo cobrado do cliente por cada missão realizada</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Franquia Tempo (min)</Label>
                      <Input type="number" value={formData.pricing[0]?.time_franchise_minutes} onChange={(e) => updatePricing('time_franchise_minutes', parseInt(e.target.value))} data-testid="customer-time-franchise-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Franquia Distância (km)</Label>
                      <Input type="number" value={formData.pricing[0]?.distance_franchise_km} onChange={(e) => updatePricing('distance_franchise_km', parseInt(e.target.value))} data-testid="customer-distance-franchise-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor Hora Excedente (R$)</Label>
                      <Input type="number" step="0.01" value={formData.pricing[0]?.extra_hour_rate} onChange={(e) => updatePricing('extra_hour_rate', parseFloat(e.target.value))} data-testid="customer-extra-hour-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor KM Excedente (R$)</Label>
                      <Input type="number" step="0.01" value={formData.pricing[0]?.extra_km_rate} onChange={(e) => updatePricing('extra_km_rate', parseFloat(e.target.value))} data-testid="customer-extra-km-input" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch checked={formData.active} onCheckedChange={(v) => setFormData({ ...formData, active: v })} data-testid="customer-active-switch" />
                  <Label>Ativo</Label>
                </div>

                <DialogFooter>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-customer-btn">
                    {editingCustomer ? 'Atualizar' : 'Criar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar clientes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-customers-input" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52" data-testid="sort-customers">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
                <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
                <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Cidade</th>
                  <th>Status</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <td className="font-medium text-slate-900">{customer.name}</td>
                    <td className="text-slate-600 font-mono text-sm">{customer.cnpj}</td>
                    <td className="text-slate-600">{customer.email}</td>
                    <td className="text-slate-600">{customer.phone}</td>
                    <td className="text-slate-600">{customer.city || '-'}</td>
                    <td>
                      <Badge className={customer.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {customer.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(customer)} data-testid={`edit-customer-${customer.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.id)} data-testid={`delete-customer-${customer.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersPage;
